import React, { useState } from 'react';
import { X, ArrowLeft, MessageSquare, Copy, Check, Loader2 } from 'lucide-react';
import { useTheme } from '../context/ThemeContext.tsx';
import { API_URL } from '../config/api.ts';

interface MemoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    onBack: () => void;
    chatId: string;
    messages: any[];
}

const MemoryModal: React.FC<MemoryModalProps> = ({
    isOpen,
    onClose,
    onBack,
    chatId,
    messages
}) => {
    const { isDarkMode } = useTheme();
    const [summary, setSummary] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);

    const handleSummarize = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_URL}/chat/${chatId}/summarize`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ messages }), // Send messages or rely on backend fetch if stored
                credentials: 'include'
            });

            const data = await response.json();

            if (data.success) {
                setSummary(data.summary);
            } else {
                setError(data.message || 'Failed to generate summary');
            }
        } catch (err) {
            console.error('Error generating summary:', err);
            setError('An error occurred while generating the summary');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCopy = () => {
        if (!summary) return;
        navigator.clipboard.writeText(summary);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    if (!isOpen) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(5px)'
        }} onClick={onClose}>
            <div style={{
                width: '500px',
                maxWidth: '90%',
                backgroundColor: isDarkMode ? '#1e293b' : 'white',
                borderRadius: '24px',
                padding: '24px',
                boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
                border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
                color: isDarkMode ? 'white' : '#1e293b',
                maxHeight: '90vh', // Limit height
                overflowY: 'auto' // Allow local scroll if content is long
            }} onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <button
                            onClick={onBack}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: isDarkMode ? 'rgba(255,255,255,0.5)' : '#64748b',
                                cursor: 'pointer',
                                padding: '4px',
                                display: 'flex',
                                borderRadius: '50%',
                                transition: 'background 0.2s',
                            }}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(255,255,255,0.1)' : '#f1f5f9'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                            <ArrowLeft size={20} />
                        </button>
                        <h2 style={{ fontSize: '20px', fontWeight: '700', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                            Auto-Summary <MessageSquare size={18} color="#a855f7" />
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            color: isDarkMode ? 'rgba(255,255,255,0.5)' : '#64748b',
                            cursor: 'pointer',
                            padding: '4px',
                            display: 'flex',
                            borderRadius: '50%',
                            transition: 'background 0.2s'
                        }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(255,255,255,0.1)' : '#f1f5f9'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                    {!summary && !isLoading && (
                        <div style={{
                            padding: '24px',
                            textAlign: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '16px'
                        }}>
                            <div style={{
                                width: '64px',
                                height: '64px',
                                borderRadius: '50%',
                                backgroundColor: 'rgba(168, 85, 247, 0.1)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#a855f7',
                                marginBottom: '8px'
                            }}>
                                <MessageSquare size={32} />
                            </div>
                            <p style={{ margin: 0, color: isDarkMode ? 'rgba(255,255,255,0.7)' : '#475569', lineHeight: '1.5' }}>
                                Generate a concise summary of your conversation history using Grok. This helps you recall key details without scrolling through thousands of messages.
                            </p>
                        </div>
                    )}

                    {/* Summarize Button */}
                    {!summary && !isLoading && (
                        <button
                            onClick={handleSummarize}
                            style={{
                                width: '100%',
                                padding: '16px',
                                borderRadius: '16px',
                                border: 'none',
                                background: 'linear-gradient(135deg, #f472b6 0%, #a855f7 100%)', // Pink gradient
                                color: 'white',
                                fontWeight: '600',
                                fontSize: '16px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                boxShadow: '0 4px 12px rgba(168, 85, 247, 0.3)',
                                transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                            }}
                            onMouseEnter={e => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 6px 16px rgba(168, 85, 247, 0.4)';
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(168, 85, 247, 0.3)';
                            }}
                        >
                            Summarize <span style={{ opacity: 0.8 }}>•</span> <span style={{ fontSize: '14px', background: 'rgba(0,0,0,0.2)', padding: '2px 8px', borderRadius: '12px' }}>💬 1</span>
                        </button>
                    )}

                    {/* Loading State */}
                    {isLoading && (
                        <div style={{
                            padding: '40px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '16px',
                            color: isDarkMode ? 'rgba(255,255,255,0.7)' : '#475569'
                        }}>
                            <Loader2 size={40} className="animate-spin" color="#a855f7" />
                            <p style={{ margin: 0, fontSize: '14px' }}>Analyzing conversation history...</p>
                        </div>
                    )}

                    {/* Error State */}
                    {error && (
                        <div style={{
                            padding: '16px',
                            borderRadius: '12px',
                            backgroundColor: 'rgba(239, 68, 68, 0.1)',
                            border: '1px solid rgba(239, 68, 68, 0.2)',
                            color: '#ef4444',
                            fontSize: '14px',
                            textAlign: 'center'
                        }}>
                            {error}
                        </div>
                    )}

                    {/* Summary Result */}
                    {summary && (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px',
                            animation: 'fadeIn 0.5s ease'
                        }}>
                            <div style={{
                                padding: '20px',
                                borderRadius: '16px',
                                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f1f5f9',
                                border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
                                maxHeight: '300px',
                                overflowY: 'auto',
                                position: 'relative'
                            }}>
                                <p style={{
                                    margin: 0,
                                    lineHeight: '1.6',
                                    fontSize: '15px',
                                    whiteSpace: 'pre-wrap',
                                    color: isDarkMode ? 'rgba(255,255,255,0.9)' : '#1e293b'
                                }}>
                                    {summary}
                                </p>
                            </div>

                            <button
                                onClick={handleCopy}
                                style={{
                                    alignSelf: 'flex-end',
                                    padding: '8px 16px',
                                    borderRadius: '12px',
                                    border: 'none',
                                    backgroundColor: copied ? '#22c55e' : (isDarkMode ? 'rgba(255,255,255,0.1)' : '#e2e8f0'),
                                    color: copied ? 'white' : (isDarkMode ? 'white' : '#1e293b'),
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    fontSize: '13px',
                                    fontWeight: '500',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {copied ? <Check size={16} /> : <Copy size={16} />}
                                {copied ? 'Copied' : 'Copy'}
                            </button>
                        </div>
                    )}

                </div>

            </div>
        </div>
    );
};

export default MemoryModal;
